import axios from 'axios';
import { authHeaders } from './authHeaders';
import {
  DashboardResponse,
  ManualRevenueEntry,
  CreateManualRevenuePayload,
  UpdateManualRevenuePayload,
  ManualRevenueMutationResponse,
  DateRange,
} from '../types/Dashboard';

/** @deprecated Payload shape used by legacy EditRevenueModal. Remove after migration. */
interface LegacyUpdateManualRevenuePayload {
  planName: string;
  monthlyAmount: number;
  effectiveMonth: string;
  notes?: string;
}

const API_URL = '/api/dashboard';

async function updateManualRevenueImpl(
  idOrPayload: number | LegacyUpdateManualRevenuePayload,
  payload?: UpdateManualRevenuePayload
): Promise<ManualRevenueMutationResponse> {
  try {
    let response;
    if (typeof idOrPayload === 'number' && payload !== undefined) {
      // New API: PATCH /manual-revenue/:id
      response = await axios.patch<ManualRevenueMutationResponse>(
        `${API_URL}/manual-revenue/${idOrPayload}`,
        payload,
        { headers: authHeaders() }
      );
    } else {
      // Legacy API: PUT /manual-revenue (single payload object)
      response = await axios.put<ManualRevenueMutationResponse>(
        `${API_URL}/manual-revenue`,
        idOrPayload,
        { headers: authHeaders() }
      );
    }
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return error.response.data as ManualRevenueMutationResponse;
    }
    return {
      success: false,
      message: 'Error al actualizar el revenue manual',
      error: error instanceof Error ? error.message : 'Error desconocido',
    };
  }
}

const dashboardService = {
  async getDashboardData(): Promise<DashboardResponse> {
    try {
      const response = await axios.get<DashboardResponse>(`${API_URL}/data`, { headers: authHeaders() });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as DashboardResponse;
      }
      return {
        success: false,
        fetchedAt: new Date().toISOString(),
        stale: false,
        premium: [],
        freeActivos: [],
        precios: {},
        manualRevenueHistory: {},
        message: 'Error al obtener el dashboard',
        error: error instanceof Error ? error.message : 'Error desconocido',
      };
    }
  },

  /**
   * @deprecated Use getDashboardData(). Kept for backward compat with DashboardPage.
   * Returns an envelope shaped like the old DashboardResponse so existing page code compiles.
   * The `premium` field is typed as `any[]` here to bridge PurchaserPlanRaw vs PremiumPlanRow mismatch.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getDashboardPlanes(): Promise<any> {
    const res = await dashboardService.getDashboardData();
    return {
      ...res,
      // Legacy consumers expect PremiumPlanRow[] — new consumer (DashboardPage) will be migrated separately.
      premium: res.premium,
      free: [] as [],
      altasPorMes: [] as [],
      bajasPorMes: [] as [],
      totals: { activosPremium: 0, activosFree: 0, revenueMensualTotal: 0 },
    };
  },

  async getManualRevenueHistory(plan: string, range?: Partial<DateRange>): Promise<ManualRevenueEntry[]> {
    try {
      const encoded = encodeURIComponent(plan);
      const params: Record<string, string> = {};
      if (range?.from) params.from = range.from;
      if (range?.to) params.to = range.to;
      const response = await axios.get(
        `${API_URL}/manual-revenue/${encoded}`,
        { headers: authHeaders(), params }
      );
      // Backend envuelve en { success, planName, entries }; tolerar array plano por las dudas.
      const body = response.data;
      if (Array.isArray(body)) return body;
      if (Array.isArray(body?.entries)) return body.entries;
      return [];
    } catch {
      return [];
    }
  },

  async createManualRevenue(payload: CreateManualRevenuePayload): Promise<ManualRevenueMutationResponse> {
    try {
      const response = await axios.post<ManualRevenueMutationResponse>(
        `${API_URL}/manual-revenue`,
        payload,
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ManualRevenueMutationResponse;
      }
      return {
        success: false,
        message: 'Error al crear el revenue manual',
        error: error instanceof Error ? error.message : 'Error desconocido',
      };
    }
  },

  /**
   * Accepts both the new two-argument form (id, payload) and the legacy
   * single-argument form ({ planName, monthlyAmount, effectiveMonth, notes }).
   * The legacy form calls PUT /manual-revenue; the new form calls PATCH /manual-revenue/:id.
   * @deprecated Single-argument form — migrate callers to updateManualRevenue(id, payload).
   */
  updateManualRevenue(
    idOrPayload: number | LegacyUpdateManualRevenuePayload,
    payload?: UpdateManualRevenuePayload
  ): Promise<ManualRevenueMutationResponse> {
    return updateManualRevenueImpl(idOrPayload, payload);
  },

  async deleteManualRevenue(id: number): Promise<ManualRevenueMutationResponse> {
    try {
      const response = await axios.delete<ManualRevenueMutationResponse>(
        `${API_URL}/manual-revenue/${id}`,
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ManualRevenueMutationResponse;
      }
      return {
        success: false,
        message: 'Error al eliminar el revenue manual',
        error: error instanceof Error ? error.message : 'Error desconocido',
      };
    }
  },

  async refreshCache(): Promise<{ success: boolean; refreshedAt?: string; message?: string }> {
    try {
      const response = await axios.post(`${API_URL}/refresh`, {}, { headers: authHeaders() });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return { success: false, message: 'Error al refrescar el cache' };
    }
  },
};

export default dashboardService;
